import { useEffect, useState } from "react"
import CalculateCard from "@/components/analysis/calculate/CalculateCard"
import ToolTips from "@/constants/ToolTips"
import FormDivider from "@/components/forms/FormDivider"
import { useEditAnalysis } from "@/queries/analysis"
import CalculatorItem from "@/components/calculate/CalculatorItem"
import { type AnalysisType } from "@/generated/graphql"
import * as AnalyzerEvents from "@/analytics/events/analyzer"
import { BFColors } from "@/constants/Colors"

interface FinancingCostsSectionProps {
  analysis: AnalysisType | null
}

const FinancingCostsSection: React.FC<FinancingCostsSectionProps> = ({
  analysis,
}: FinancingCostsSectionProps) => {
  const [leverage, setLeverage] = useState<number | null | undefined>()
  const [interestRate, setInterestRate] = useState<number | null | undefined>()
  const [financingFees, setFinancingFees] = useState<
    number | null | undefined
  >()
  const editAnalysisMutation = useEditAnalysis()

  useEffect(() => {
    if (analysis) {
      const ltc = analysis.loanToCostAtCompletion || 0
      const interestRt = analysis.interestRate || 0
      setLeverage(ltc <= 1 ? ltc * 100 : ltc)
      setInterestRate(interestRt <= 1 ? interestRt * 100 : interestRt)
      setFinancingFees(analysis.financingFees)
    }
  }, [analysis])

  if (!analysis) {
    return null
  }

  const handleBlur = (title: string, value: number | null | undefined) => {
    AnalyzerEvents.analysisEditSubmitted({
      analysis,
      editedField: {
        fieldName: title,
        newValue: value,
      },
      fromScreen: "Subject Property Calculate",
      toScreen: "Subject Property Calculate",
    })
    editAnalysisMutation.mutate({
      analysisId: analysis?.id,
      analysisInput: {
        loanToCostAtCompletion: (leverage || 0) / 100,
        interestRate: (interestRate || 0) / 100,
        financingFees,
      },
    })
  }

  return (
    <CalculateCard title="Leverage & Financing Costs">
      <CalculatorItem
        title="Leverage (%)"
        value={leverage}
        tooltip={ToolTips.leverage}
        prefix=""
        onBlur={() => {
          handleBlur("Leverage & Financing Costs", leverage)
        }}
        onValueChange={setLeverage}
      />
      <CalculatorItem title="Total Loan Amt" value={analysis.totalLoanAmount} />
      <FormDivider />
      <CalculatorItem
        title="Interest Rate (%)"
        value={interestRate}
        tooltip={ToolTips.interestRate}
        prefix=""
        // suffix="%"
        precision={1}
        onBlur={() => {
          handleBlur("Interest Rate", interestRate)
        }}
        onValueChange={setInterestRate}
      />
      <CalculatorItem
        title="Payment (mo)"
        value={analysis.monthlyPayment}
        suffix="/mo"
      />
      <FormDivider />
      <CalculatorItem
        title="Financing Fees!"
        textColor={BFColors.midnightBlue}
        value={financingFees}
        tooltip={ToolTips.oneTimeFinancing}
        onBlur={() => {
          handleBlur("Financing Fees", financingFees)
        }}
        onValueChange={setFinancingFees}
      />
    </CalculateCard>
  )
}

export default FinancingCostsSection
